// addAppChatFollow.js — THE BUILDER'S CONVERSATION FOLLOWS ITS NEWEST MESSAGE
// (persona round 1b, BLD-16: priya, elena, nino, marcus, giorgi — "the chat
// column did not scroll, so I saw the failure only after I pressed End"; "the
// answer buttons sit under the clipped edge"; "a box about 350 px tall that
// sits inside a card inside the page").
//
// Measured on dev (workspace a2c0b97e5, 2026-10-25; the builder with 20 rows):
//   1. after the person scrolls up, their OWN new message stays under the fold
//      (gap 404 px): the shared Chat re-pins in its Messages `onUpdate`, and
//      smbls 3.14 never runs a descendant's onUpdate for a root-state write
//      (SYNTAX, "Which hook runs on which state change");
//   2. at the bottom, a six-line draft grows the composer by 95 px and the
//      newest 95 px slide under it while the list still counts as pinned —
//      the shared follow watches the message stack's size, never its
//      scroller's;
//   3. at 390 px the message box is 221 px tall inside AppMain, which scrolls
//      too (the panes stack there): one box scrolling inside another.
//
// The builder's own fix (the shared Chat is read-only for this package):
//   - every change of the thread's rows follows (pages/add-app.js Messages
//     `stateDeps`): a new row of the PERSON re-pins and brings the end into
//     view; anything else follows only while pinned, so a person who
//     scrolled up keeps their place;
//   - the composer growing (a draft) and the window resizing follow too;
//   - on the stacked layout the conversation is no inner box: it reads with
//     the page (AppMain is its one scroller) and the composer sticks to the
//     bottom (pages/add-app.js). The follow then scrolls AppMain to the
//     conversation's end, and AppMain's own scroll says whether the person
//     is at that end (addAppChatTrack).
// The pinned flag is the shared Chat's own — `_pinned` on the Messages
// element, kept by its scroll handler on the wide layout — so the shared
// follow (a ResizeObserver on the rows) and this one never disagree.
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.
// The scroll moves through `scrollTo()`: reading and scrolling a node is
// allowed, a property write is FA511 (functions/agentModeScroll.js).

// The conversation's scroller and the top that shows its end →
// { node, top, inner } or null. `inner`: the message list scrolls itself (the
// wide layout); else AppMain does (the stacked layout), and `top` puts the
// conversation's last edge — the composer — at the bottom of the view.
export function addAppChatScroller(messages) {
  const el = messages
  const node = el && el.node
  if (!node || node.isConnected === false) return null
  const view = node.ownerDocument && node.ownerDocument.defaultView
  const scrolls = (n) => {
    if (!n || !view || typeof view.getComputedStyle !== 'function') return false
    const css = view.getComputedStyle(n)
    // the longhand, else the shorthand (a document without layout keeps only it)
    const overflowY = css.overflowY || css.overflow
    return (overflowY === 'auto' || overflowY === 'scroll') && n.scrollHeight > n.clientHeight + 1
  }
  if (scrolls(node)) return { node, top: node.scrollHeight, inner: true }
  const main = typeof el.lookup === 'function' ? el.lookup('AppMain') : null
  const conversation = typeof el.lookup === 'function' ? el.lookup('Conversation') : null
  const outer = main && main.node
  if (!outer || !scrolls(outer)) return null
  const end = (conversation && conversation.node) || node
  const box = outer.getBoundingClientRect()
  const edge = end.getBoundingClientRect()
  return {
    node: outer,
    top: Math.max(0, outer.scrollTop + (edge.bottom - box.bottom)),
    inner: false
  }
}

// FOLLOW — after a change of the thread (`prev` / `next`: its rows before and
// after, from the Messages onStateUpdate), of the composer or of the window.
// A new row of the person re-pins; anything else moves only while pinned. One
// move per frame. → true when a move is on its way.
export function addAppChatFollow(messages, prev, next) {
  const el = messages
  const node = el && el.node
  if (!node || node.isConnected === false) return false
  const before = Array.isArray(prev) ? prev : []
  const after = Array.isArray(next) ? next : []
  // A row of the person among the rows that came in: the send writes the
  // person's row and the transport its answer's placeholder in the same tick,
  // so the newest row is often the placeholder. A thread that opens arrives
  // whole the same way — and opens at its end.
  const added = after.length > before.length ? after.slice(before.length) : []
  const own = added.some((row) => !!row && row.role === 'user')
  if (own) el._pinned = true
  else if (el._pinned === false) return false
  if (el.__addAppFollow) return true
  el.__addAppFollow = true
  const self = this
  const view = node.ownerDocument && node.ownerDocument.defaultView
  const frame =
    view && typeof view.requestAnimationFrame === 'function'
      ? (fn) => view.requestAnimationFrame(fn)
      : (fn) => setTimeout(fn, 16)
  frame(() => {
    el.__addAppFollow = false
    if (node.isConnected === false) return
    const at = self.call('addAppChatScroller', el)
    if (at && typeof at.node.scrollTo === 'function') at.node.scrollTo({ top: at.top })
  })
  return true
}

// TRACK — the stacked layout's scroll (AppMain onScroll): is the person at the
// conversation's end — within 120 px of it, above or below (the canvas sits
// under the conversation there, and a person reading it is not pinned)? The
// wide layout's own scroll handler keeps the flag; there AppMain never scrolls.
export function addAppChatTrack(main) {
  const conversation = main && main.Conversation
  const messages = conversation && conversation.Chat && conversation.Chat.Messages
  const node = main && main.node
  if (!messages || !node || !conversation.node) return false
  const at = this.call('addAppChatScroller', messages)
  if (!at || at.inner || at.node !== node) return false
  const gap = conversation.node.getBoundingClientRect().bottom - node.getBoundingClientRect().bottom
  messages._pinned = Math.abs(gap) < 120
  return true
}

// SHOW THE APP — where the panes stack (a phone, a split pane), bring the
// canvas into view: AppMain scrolls so the canvas starts at the top of the
// view (persona round 3, W2, marcus at 390 px: "the built app preview sits
// BELOW the chat … I spent ~12 taps looking for it"; a tap on the app in Your
// apps changed nothing on screen). The conversation stops following its end
// (`_pinned` false), so an answer that streams in after the build does not
// pull the column back up to the chat; the person's own next message re-pins
// it (addAppChatFollow). Side by side the canvas is on screen already: nothing
// moves. `from`: AppMain, or any element of the builder page (the Your apps
// row, the conversation head). One move per frame. → true when a move is on
// its way.
export function addAppShowCanvas(from) {
  const el = from
  if (!el || typeof el !== 'object') return false
  const findMain = () => {
    if (el.key === 'AppMain') return el
    let at = el
    while (at) {
      if (at.AppMain && at.AppMain.BuildCanvas) return at.AppMain
      at = at.parent
    }
    return null
  }
  const main = findMain()
  const node = main && main.node
  const canvas = main && main.BuildCanvas && main.BuildCanvas.node
  if (!node || !canvas || node.isConnected === false) return false
  const view = node.ownerDocument && node.ownerDocument.defaultView
  const conversation = main.Conversation
  const messages = conversation && conversation.Chat && conversation.Chat.Messages
  const frame =
    view && typeof view.requestAnimationFrame === 'function'
      ? (fn) => view.requestAnimationFrame(fn)
      : (fn) => setTimeout(fn, 16)
  // stacked = AppMain is the scroller (the wide layout never scrolls it)
  const stacked = () => {
    if (!view || typeof view.getComputedStyle !== 'function') return false
    const css = view.getComputedStyle(node)
    const overflowY = css.overflowY || css.overflow
    return (
      (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1
    )
  }
  // Unpinned at once, not in the frame: a follow asked for before the frame
  // (a row that lands in the same tick) must already see it.
  if (stacked() && messages) messages._pinned = false
  if (main.__addAppShowCanvas) return true
  main.__addAppShowCanvas = true
  frame(() => {
    main.__addAppShowCanvas = false
    if (node.isConnected === false || !stacked()) return
    if (messages) messages._pinned = false
    const top =
      node.scrollTop + (canvas.getBoundingClientRect().top - node.getBoundingClientRect().top)
    if (typeof node.scrollTo === 'function') node.scrollTo({ top: Math.max(0, top) })
  })
  return true
}
